import compact from 'lodash/compact';

import { DEFAULT_CURRENCY } from '~/components/Offers/Detail/OfferBuilder/DealInformation/tabs/dealOptions/dealOptionsFormatters';

import type { OpportunityRelatedDeal } from '~/hooks/apis/offers/useGetOpportunityRelated';

import { formatAudCurrency, formatNumber } from '~/utils/numberFormatters';

import { PRICING_OPTION_LABELS } from '../../constants';
import type { PricingOptionIndex } from '../../types';
import { PACKAGE_CATEGORIES } from '../packageOptions/packageCategories';
import { DEFAULT_OPTION_STATE } from '../packageOptions/packageOptionsModel';
import { roomDisplayName, roomVariantKey } from '../packageOptions/types';
import type { CreatedAsset, OptionState, PackageOptionId, RoomAssetLink } from '../packageOptions/types';

import { INCLUSION_FIELD_LABELS } from './fields';
import type { InclusionApplyTo, InclusionDraft, InclusionDraftPayload } from './types';

export function formatInclusionPrv(value: number | undefined) {
  if (value === undefined || value === null || Number.isNaN(value)) {
    return '—';
  }
  return formatNumber(value, 2, { pad: false });
}

// See docs/business-rules.md#prv-value-display.
const DEFAULT_LEAD_GUESTS = 2;

// See docs/business-rules.md#salesforce-data-model.
export function deriveOpportunityCurrency(deals: Array<OpportunityRelatedDeal> = []) {
  return deals.find((deal) => deal.currencyisocode)?.currencyisocode ?? DEFAULT_CURRENCY;
}

export interface PackageRoomRow {
  id: PackageOptionId;
  label: string;
  occupancy: string;
}

// See docs/business-rules.md#package-options-source.
export function getPackageRooms(option: OptionState): Array<PackageRoomRow> {
  const labelCounts = new Map<string, number>();
  return option.roomsByCategory.flatMap((rooms, categoryIndex) =>
    rooms
      .filter((room) => room.assetName)
      .map((room) => {
        const category = PACKAGE_CATEGORIES[categoryIndex];
        const baseLabel = roomDisplayName(room);
        const count = (labelCounts.get(baseLabel) ?? 0) + 1;
        labelCounts.set(baseLabel, count);
        return {
          id: room.id,
          label: count > 1 ? `${baseLabel} #${count}` : baseLabel,
          occupancy: option.pricingByRoomId[room.id]?.occupancy ?? category.defaultOccupancy,
        };
      }),
  );
}

export function getAvailableOccupancies(rooms: Array<PackageRoomRow>) {
  return Array.from(new Set(rooms.map((room) => room.occupancy))).sort((occupancyA, occupancyB) =>
    occupancyA.localeCompare(occupancyB),
  );
}

// No rooms means no packages, so LOS stays blank too.
export function getAvailableNights(option: OptionState) {
  if (!getPackageRooms(option).length) {
    return [];
  }
  return option.lengthsOfStay.slice().sort((nightsA, nightsB) => nightsA - nightsB);
}

export function getLeadNights(option: OptionState) {
  return getAvailableNights(option)[0];
}

export function defaultApplyTo(): InclusionApplyTo {
  return { roomTypes: 'all', occupancies: 'all', los: 'all' };
}

// See docs/business-rules.md#applies-to-filtering.
export function packageMatchesApplyTo(room: PackageRoomRow, nights: number, applyTo: InclusionApplyTo) {
  return (
    (applyTo.roomTypes === 'all' || applyTo.roomTypes.includes(room.id)) &&
    (applyTo.occupancies === 'all' || applyTo.occupancies.includes(room.occupancy)) &&
    (applyTo.los === 'all' || applyTo.los.includes(nights))
  );
}

// See docs/business-rules.md#applies-to-filtering.
export function getApplyToChoices(rooms: Array<PackageRoomRow>, nights: Array<number>, applyTo: InclusionApplyTo) {
  const packages = rooms.flatMap((room) => nights.map((nightCount) => ({ room, nightCount })));
  const matching = (ignored?: keyof InclusionApplyTo) =>
    packages.filter(({ room, nightCount }) =>
      packageMatchesApplyTo(room, nightCount, ignored ? { ...applyTo, [ignored]: 'all' } : applyTo),
    );

  return {
    matchCount: matching().length,
    roomIds: new Set(matching('roomTypes').map(({ room }) => room.id)),
    occupancies: new Set(matching('occupancies').map(({ room }) => room.occupancy)),
    nights: new Set(matching('los').map(({ nightCount }) => nightCount)),
  };
}

// With no rooms there's nothing to match, so the untouched default stays applicable.
export function isApplyToValid(rooms: Array<PackageRoomRow>, nights: Array<number>, applyTo: InclusionApplyTo) {
  return rooms.length === 0 || getApplyToChoices(rooms, nights, applyTo).matchCount > 0;
}

// Room ids differ per option, so pair rows by category + name + nth occurrence.
function packageRowKeys(option: OptionState) {
  const seen = new Map<string, number>();
  const keys = new Map<PackageOptionId, string>();
  option.roomsByCategory.forEach((rooms, categoryIndex) =>
    rooms.forEach((room) => {
      const base = `${categoryIndex}::${roomVariantKey(room)}`;
      const nth = seen.get(base) ?? 0;
      seen.set(base, nth + 1);
      keys.set(room.id, `${base}#${nth}`);
    }),
  );
  return keys;
}

// See docs/business-rules.md#copying-between-options.
export function copyDraftsToOption(drafts: Array<InclusionDraft>, source: OptionState, target: OptionState) {
  const sourceKeys = packageRowKeys(source);
  const targetIdByKey = new Map(Array.from(packageRowKeys(target), ([id, key]) => [key, id]));

  const targetRooms = getPackageRooms(target);
  const targetNights = getAvailableNights(target);

  return drafts.flatMap((draft) => {
    const { roomTypes } = draft.applyTo;
    if (roomTypes === 'all') {
      return isApplyToValid(targetRooms, targetNights, draft.applyTo) ? [draft] : [];
    }

    const targetRoomTypes = roomTypes.flatMap((id) => {
      const targetId = targetIdByKey.get(sourceKeys.get(id) ?? '');
      return targetId === undefined ? [] : [targetId];
    });
    const applyTo = { ...draft.applyTo, roomTypes: targetRoomTypes };
    // isApplyToValid passes with no target rooms, but an emptied list would never match later.
    if (roomTypes.length > 0 && targetRoomTypes.length === 0) {
      return [];
    }
    return isApplyToValid(targetRooms, targetNights, applyTo) ? [{ ...draft, applyTo }] : [];
  });
}

export function describeApplyTo(applyTo: InclusionApplyTo, rooms: Array<PackageRoomRow>) {
  if (applyTo.roomTypes === 'all' && applyTo.occupancies === 'all' && applyTo.los === 'all') {
    return 'All packages';
  }

  const parts: Array<string> = [];
  const { roomTypes } = applyTo;
  if (roomTypes !== 'all') {
    const pickedRooms = rooms.filter((room) => roomTypes.includes(room.id));
    parts.push(pickedRooms.length === 1 ? pickedRooms[0].label : `${pickedRooms.length} room types`);
  }
  if (applyTo.occupancies !== 'all') {
    parts.push(applyTo.occupancies.length === 1 ? applyTo.occupancies[0] : `${applyTo.occupancies.length} occupancies`);
  }
  if (applyTo.los !== 'all') {
    parts.push(applyTo.los.length === 1 ? `${applyTo.los[0]}N` : `${applyTo.los.length} LOS`);
  }
  return parts.join(' · ') || 'No packages selected';
}

// See docs/business-rules.md#prv-value-display.
export function describeInclusionValue({
  prv,
  type,
  leadNights,
}: {
  prv: number | undefined;
  type?: string;
  leadNights?: number;
}) {
  if (!prv) {
    return 'No value set - contributes nothing to PRV';
  }

  const bits = [`${formatAudCurrency(prv)} total value`];

  if (type === 'Daily' && leadNights && leadNights > 1) {
    const perNight = prv / leadNights;
    const roundedPerNight = perNight < 10 ? perNight.toFixed(2) : perNight.toFixed(0);
    bits.push(`≈ $${roundedPerNight} per night across ${leadNights}N for ${DEFAULT_LEAD_GUESTS}`);
  }

  return bits.join(' · ');
}

export function sumInclusionValue(drafts: Array<InclusionDraft>) {
  return drafts.reduce((sum, draft) => sum + (draft.prv ?? 0), 0);
}

export interface PackageInclusionRow {
  id: string;
  name: string;
  nightCount: number;
  draftInclusions: Array<InclusionDraft>;
}

export interface PackageInclusionGroup {
  id: PackageOptionId;
  roomName: string;
  deals: Array<PackageInclusionRow>;
}

export function buildPackageInclusionGroups(
  option: OptionState,
  drafts: Array<InclusionDraft>,
): Array<PackageInclusionGroup> {
  const nights = getAvailableNights(option);
  return getPackageRooms(option).map((room) => ({
    id: room.id,
    roomName: room.label,
    deals: nights.map((nightCount) => ({
      id: `${room.id}-${nightCount}`,
      name: `${room.occupancy} · ${nightCount}N`,
      nightCount,
      draftInclusions: drafts.filter((draft) => packageMatchesApplyTo(room, nightCount, draft.applyTo)),
    })),
  }));
}

const normaliseAssetName = (name: string) => name.trim().replace(/\s+/g, ' ').toLowerCase();

// See docs/business-rules.md#inclusion-assets.
export function findReusableInclusionAsset(
  name: string,
  createdAssets: Array<CreatedAsset>,
  accountAssets: Array<RoomAssetLink>,
): RoomAssetLink | undefined {
  const key = normaliseAssetName(name);
  const created = createdAssets.find((asset) => normaliseAssetName(asset.name) === key);
  if (created) {
    return created;
  }
  const accountMatches = accountAssets.filter((asset) => normaliseAssetName(asset.name) === key);
  return accountMatches.length === 1 ? accountMatches[0] : undefined;
}

// An empty `missing` means every field is filled in but the inclusion applies to no package.
interface InclusionProblem {
  pricingOptionIndex: number;
  name: string;
  missing: Array<string>;
}

function findInclusionProblem(
  draft: InclusionDraftPayload,
  optionStates: Record<number, OptionState>,
  submittedOption: number | null,
): InclusionProblem | undefined {
  const option = optionStates[draft.pricingOptionIndex] ?? DEFAULT_OPTION_STATE;
  const matchesAnyPackage = isApplyToValid(getPackageRooms(option), getAvailableNights(option), draft.applyTo);
  const submitted = draft.pricingOptionIndex === submittedOption;
  const missing = compact([
    !draft.name.trim() && INCLUSION_FIELD_LABELS.name,
    !draft.assetLink && 'Asset',
    draft.rrp !== undefined && draft.rrp < 0 && INCLUSION_FIELD_LABELS.rrp,
    // svc-offer saves Type empty, so only the submitted option requires it.
    submitted && !draft.type && INCLUSION_FIELD_LABELS.type,
  ]);
  return missing.length > 0 || !matchesAnyPackage
    ? { pricingOptionIndex: draft.pricingOptionIndex, name: draft.name.trim(), missing }
    : undefined;
}

// svc-offer rejects a session with any of these missing, so saving is blocked up front with a readable reason.
export function findInclusionProblems(
  drafts: Array<InclusionDraftPayload>,
  optionStates: Record<number, OptionState>,
  submittedOption: PricingOptionIndex | null,
): Array<InclusionProblem> {
  return compact(drafts.map((draft) => findInclusionProblem(draft, optionStates, submittedOption)));
}

export function isUnfinishedInclusion(
  draft: InclusionDraftPayload,
  optionStates: Record<number, OptionState>,
): boolean {
  return findInclusionProblem(draft, optionStates, draft.pricingOptionIndex) !== undefined;
}

export function describeIncompleteInclusion(
  drafts: Array<InclusionDraftPayload>,
  optionStates: Record<number, OptionState>,
): string | undefined {
  const [first] = findInclusionProblems(drafts, optionStates, null);
  if (!first) {
    return undefined;
  }
  const label = first.name ? `"${first.name}"` : 'unnamed inclusion';
  const problem = first.missing.length > 0 ? `is missing ${first.missing.join(', ')}` : 'applies to no package';
  return `${PRICING_OPTION_LABELS[first.pricingOptionIndex]}: ${label} ${problem}`;
}
